import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath, SettingsTabsEnum } from '@/Config/app/router/index.path';
import { type KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { useControllableState } from '@/Domain/hooks/Shared/useControllableState.hook';
import { SCENARIO_ID } from '@/context';

type DeleteAIModalProps = {
  /** Controlled open state. When provided, the inline trigger card is hidden. */
  open?: boolean;
  /** Controlled open-change callback. Required when `open` is provided. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Scenario id of the AI agent to delete. Defaults to the current scenario
   * (the one bound to the page URL). Provide a non-current scenario_id to
   * delete a different agent — the request gains a `scenario-id` header
   * override.
   */
  scenarioId?: string;
  /** Display name for the targeted agent — shown in the dialog body when set. */
  agentName?: string;
  /**
   * Pricing tier of the targeted agent. Used to decide whether to show the
   * "cancel subscription first" warning instead of the destructive action,
   * since the backend only deletes trial-tier scenarios. Required when
   * `scenarioId` is provided; ignored for the uncontrolled (current-scenario)
   * path which uses `useSubscription().isActivated('can_delete')` instead.
   */
  agentPricingTier?: TierEnum;
};

export default function DeleteAIModal({
  open: openProp,
  onOpenChange,
  scenarioId,
  agentName,
  agentPricingTier,
}: DeleteAIModalProps = {}) {
  const { t } = useTranslation(['user_profile', 'common']);
  const { data: { one_word_description } = {} } = useQuery<KnowledgeBase>({
    queryKey: ['knowledge_base'],
  });
  const queryClient = useQueryClient();
  const account = queryClient.getQueryData<AccountDto>(['account']);

  const isCurrentScenario = scenarioId == null || scenarioId === SCENARIO_ID;

  // Stays true once the mutation succeeds — keeps the button locked + spinner
  // visible across the brief window between onSuccess and `window.location.reload()`
  // so the user doesn't see a flash of "Delete" text right before the page reloads.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { mutate, isPending } = useMutation<void, unknown, string | undefined>({
    mutationKey: [QueryKey.implementation.delete],
    onSuccess: () => {
      setHasSubmitted(true);
      window.location.reload();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        t('delete.error', { defaultValue: 'Failed to delete AI agent. Please try again.' });
      toast.error(message);
    },
  });

  const isBusy = isPending || hasSubmitted;

  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const navigate = useNavigate();
  const isControlled = openProp !== undefined;
  const [open, setOpenInternal] = useControllableState(openProp, onOpenChange, false);
  // Block close attempts (Esc, overlay click, Cancel button) while the
  // destructive mutation is in flight so the user can't dismiss mid-delete.
  const setOpen = (next: boolean) => {
    if (isBusy && !next) return;
    setOpenInternal(next);
  };

  // Tier check: the backend only deletes free-tier scenarios (legacy trial or
  // new free). For the current scenario this is read from the loaded
  // subscription; for a per-agent caller it comes from the AIProfile.pricing_tier.
  const targetIsTrialTier = isCurrentScenario
    ? isActivated('can_delete')
    : agentPricingTier === TierEnum.TRIAL || agentPricingTier === TierEnum.FREE;
  // Don't flash the "paid plan needed" warning while isActivated('can_delete') is still false during subscription load.
  const showSubscriptionWarning =
    (!isCurrentScenario || isSubscriptionFetched) && !targetIsTrialTier;
  const isLastScenario = (account?.scenarios.length || 0) === 1;
  // The destructive Delete action only renders when:
  //  - the agent is on TRIAL (otherwise the backend's
  //    `can_remove_implementation` would 403), AND
  //  - it isn't the only agent on the account. The body shows the "you
  //    cannot delete the only agent on your account" message in that case;
  //    rendering Delete here would contradict it.
  const showDeleteAction = targetIsTrialTier && !isLastScenario;
  // "Go to billing" only makes sense when the user has a path forward:
  // they're on a paid tier (so they need to cancel) AND they have other
  // agents. Last-scenario users on paid tiers fall through to the OK
  // dismissal branch — same as last-scenario users on trial.
  const showGoToBilling = showSubscriptionWarning && !isLastScenario;
  const targetScenarioId = scenarioId ?? SCENARIO_ID;
  const handleGoToBilling = () => {
    setOpen(false);
    navigate(`/i/${targetScenarioId}/${IndexPath.settings}?tab=${SettingsTabsEnum.Billing}`);
  };
  const titleName = agentName ?? one_word_description;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <div className="flex items-center justify-between border border-border rounded-xl relative p-6">
          <section className="flex items-center gap-2">
            <CircleAlert className="size-4 p-2 box-content text-red-500 bg-red-100 rounded-lg" />
            <h2 className="font-semibold text-foreground">{t('delete.title')}</h2>
          </section>
          <AlertDialogTrigger asChild>
            <Button variant="danger">{t('delete.button.action')}</Button>
          </AlertDialogTrigger>
        </div>
      )}

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-red-100 rounded-lg text-red-500 p-1.5 box-content" />
            {t('delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {(account?.scenarios.length || 0) === 1 ? (
                <p>
                  <Trans i18nKey="delete.last" t={t} />
                </p>
              ) : showSubscriptionWarning ? (
                <p>{t('delete.subscription')}</p>
              ) : (
                <p>
                  <Trans i18nKey="delete.description" t={t} components={{ bold: <b /> }} />
                </p>
              )}
              {titleName && (
                <p className="text-sm text-foreground">
                  {t('name', { ns: 'common' })}: <span className="font-semibold">{titleName}</span>
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {showDeleteAction ? (
            <>
              <AlertDialogCancel asChild disabled={isBusy}>
                <Button
                  variant="secondary"
                  disabled={isBusy}
                  className="disabled:!border-border disabled:!bg-card disabled:!text-foreground"
                >
                  {t('cancel', { ns: 'common' })}
                </Button>
              </AlertDialogCancel>
              <Button
                variant="danger"
                disabled={isBusy}
                onClick={() => mutate(isCurrentScenario ? undefined : scenarioId)}
                className="relative disabled:!border-destructive disabled:!bg-destructive disabled:!text-destructive-foreground"
                data-testid="delete-ai-agent-confirm-button"
              >
                <span className={isBusy ? 'invisible' : ''}>{t('delete.button.delete')}</span>
                {isBusy && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <Spinner className="h-4 w-4 text-white" />
                  </span>
                )}
              </Button>
            </>
          ) : showGoToBilling ? (
            <>
              <AlertDialogCancel asChild>
                <Button variant="secondary">{t('cancel', { ns: 'common' })}</Button>
              </AlertDialogCancel>
              <Button
                onClick={handleGoToBilling}
                data-testid="delete-ai-agent-go-to-billing-button"
              >
                {t('delete.button.go_to_billing')}
              </Button>
            </>
          ) : (
            <AlertDialogCancel asChild>
              <Button variant="secondary">{t('ok', { ns: 'common' })}</Button>
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
